import React, { useState, useEffect, useRef } from 'react';
import {
  FaUserGraduate, FaBuilding, FaCalendarAlt, FaIdBadge, FaUserTie,
  FaPhoneAlt, FaEnvelope, FaMapMarkerAlt, FaSchool,
  FaArrowLeft, FaSave, FaVenusMars, FaInfoCircle, FaEye,
  FaFileExcel, FaUpload, FaDownload
} from 'react-icons/fa';
import { useNavigate, Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx-js-style';
import api from '../../../../utils/api';
import { useAuth } from '../../../../context/AuthContext';
import useColleges from '../../../../utils/useColleges';
import { toTitleCase, phoneChange, isValidPhone, INDIAN_STATES } from '../../../../utils/formHelpers';
import StateSelect from '../../../../components/common/StateSelect/StateSelect';
import './AddStudent.css';

const AddStudent = () => {
  const { user } = useAuth();
  const isAdmin  = user?.role === 'admin' || (user?.colleges?.length > 0);
  const { colleges, loading: collegesLoading } = useColleges();
  const navigate = useNavigate();
  const basePath = user?.role === 'collegeadmin' ? '/collegeadmin' : '/admin';

  const fileInputRef = useRef(null);
  const excelInputRef = useRef(null);

  // ── Options States ──
  const [activeSession, setActiveSession] = useState(null);
  const [classes, setClasses]             = useState([]);
  const [programDuration, setProgramDuration] = useState('');
  const [nextAdmissionNo, setNextAdmissionNo] = useState('');
  const [showBulkImport, setShowBulkImport] = useState(false);

  // ── Form State ──
  const [formData, setFormData] = useState({
    college:          '',
    session:          '',
    classId:          '',
    admissionDate:    new Date().toISOString().split('T')[0],
    admissionNo:      '',
    admissionYear:    '',
    currentYear:      '1st Year',
    name:             '',
    dob:              '',
    gender:           '',
    category:         '',
    religion:         '',
    fatherName:       '',
    fatherOccupation: '',
    fatherContact:    '',
    alternativeContact: '',
    motherName:       '',
    motherOccupation: '',
    contact:          '',
    email:            '',
    address:          '',
    city:             '',
    state:            '',
    country:          'India',
    lastSchoolName:   '',
    lastSchoolClass:  '',
    lastSchoolResult: '',
    leftReason:       '',
  });

  const [photo, setPhoto]               = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [educationDoc, setEducationDoc] = useState(null);
  const [educationDocName, setEducationDocName] = useState('');
  const [saving, setSaving]             = useState(false);

  // ── Auto-set College ──
  useEffect(() => {
    if (!isAdmin && colleges.length > 0)
      setFormData(prev => ({ ...prev, college: String(colleges[0]._id) }));
    else if (isAdmin && colleges.length === 1)
      setFormData(prev => ({ ...prev, college: String(colleges[0]._id) }));
  }, [isAdmin, colleges]);

  // ── Fetch Active Session & Classes when College Changes ──
  useEffect(() => {
    if (!formData.college) {
      setActiveSession(null);
      setClasses([]);
      setFormData(prev => ({ ...prev, session: '', classId: '' }));
      return;
    }
    // Active session for this college only
    api.get('/sessions', { params: { college: formData.college } })
      .then(res => {
        const active = (res.data.data || []).find(s => s.isActive) || null;
        setActiveSession(active);
        const batchYear = active?.name ? active.name.split('-')[0].trim() : String(new Date().getFullYear());
        setFormData(prev => ({ ...prev, session: active?._id || '', classId: '', admissionYear: batchYear }));
        // Fetch next admission number
        if (active?._id) {
          api.get('/students/next-admission-no', { params: { college: formData.college, session: active._id } })
            .then(r => setNextAdmissionNo(r.data.admissionNo || ''))
            .catch(() => setNextAdmissionNo(''));
        } else {
          setNextAdmissionNo('');
        }
      })
      .catch(() => { setActiveSession(null); setNextAdmissionNo(''); });
    // Classes for this college only
    api.get('/classes', { params: { college: formData.college } })
      .then(res => setClasses(res.data.data || []))
      .catch(() => setClasses([]));
  }, [formData.college]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const titleFields = ['name','fatherName','motherName','fatherOccupation','motherOccupation','address','city','lastSchoolName'];
    const phoneFields = ['contact','fatherContact','alternativeContact'];
    if (phoneFields.includes(name)) {
      let digits = value.replace(/\D/g, '');
      if (digits.length > 0 && !/^[5-9]/.test(digits)) digits = digits.slice(1);
      if (digits.length > 10) digits = digits.slice(0, 10);
      setFormData(prev => ({ ...prev, [name]: digits, ...(name === 'college' ? { session: '', classId: '' } : {}) }));
      return;
    }
    const finalValue = titleFields.includes(name) ? toTitleCase(value) : value;
    setFormData(prev => ({
      ...prev,
      [name]: finalValue,
      ...(name === 'college' ? { session: '', classId: '' } : {}),
    }));
    if (name === 'classId') {
      const selected = classes.find(c => String(c._id) === String(value));
      setProgramDuration(selected?.duration || '');
    }
    if (name === 'college') setProgramDuration('');
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleEducationDocChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check if it's a PDF
      if (file.type !== 'application/pdf') {
        Swal.fire({ icon: 'warning', title: 'Invalid File', text: 'Only PDF files are allowed.', confirmButtonColor: '#f59e0b' });
        e.target.value = '';
        return;
      }
      setEducationDoc(file);
      setEducationDocName(file.name);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.college) {
      Swal.fire({ icon: 'warning', title: 'Select College', text: 'Please select a college first.', confirmButtonColor: '#f59e0b' });
      return;
    }
    if (!activeSession) {
      Swal.fire({ icon: 'warning', title: 'No Active Session', text: 'Please activate a session for this college before admitting a student.', confirmButtonColor: '#f59e0b' });
      return;
    }
    setSaving(true);
    try {
      const data = new FormData();
      Object.keys(formData).forEach(key => {
        data.append(key === 'classId' ? 'class' : key, formData[key]);
      });
      if (photo) data.append('photo', photo);
      if (educationDoc) data.append('educationDocument', educationDoc);
      const res = await api.post('/students', data, { headers: { 'Content-Type': 'multipart/form-data' } });
      const savedAdmNo = res.data.data?.admissionNo || '';
      await Swal.fire({
        icon: 'success',
        title: 'Student Admitted!',
        html: `Student admitted successfully.<br/><b>Admission No: ${savedAdmNo}</b>`,
        confirmButtonColor: '#10b981'
      });
      navigate(`${basePath}/students/add`);
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: error.response?.data?.message || 'Failed to add student.', confirmButtonColor: '#ef4444' });
    } finally {
      setSaving(false);
    }
  };

  // Helper function to convert DD-MM-YYYY to YYYY-MM-DD
  const convertDDMMYYYYtoYYYYMMDD = (dateStr) => {
    if (!dateStr) return '';
    const str = String(dateStr).trim();
    // Check if already in YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
    // Convert DD-MM-YYYY to YYYY-MM-DD
    const parts = str.split(/[-\/]/);
    if (parts.length === 3) {
      const [day, month, year] = parts;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return str;
  };

  // Download Excel Template
  const handleDownloadTemplate = () => {
    if (!formData.college) {
      Swal.fire('Select College', 'Please select a college first to download template with available classes.', 'warning');
      return;
    }

    const headers = [
      'Student Name*', 'Date of Birth (DD-MM-YYYY)*', 'Gender*', 'Contact Number*',
      'Admission Date (DD-MM-YYYY)*', 'Batch (Year)*', 'Current Year*',
      'Class/Course*', 'Medium', 'Program Duration', 'Father Name*', 'Father Contact*',
      'Mother Name*', 'Address*', 'City', 'State', 'Email', 'Category', 'Religion',
      'Father Occupation', 'Mother Occupation', 'Alternative Contact',
      'Last School Name', 'Last School Class', 'Last School Result', 'Reason for Leaving'
    ];

    // Add one dummy row with all required fields including class and medium
    const classRows = [];
    if (classes.length > 0) {
      const firstClass = classes[0];
      classRows.push([
        'Rahul Kumar', // Student Name*
        '15-05-2005', // Date of Birth (DD-MM-YYYY)*
        'Male', // Gender*
        '9876543210', // Contact Number*
        '01-04-2024', // Admission Date (DD-MM-YYYY)*
        '2024', // Batch (Year)*
        '1st Year', // Current Year*
        firstClass.name, // Class/Course* - ACTUAL CLASS NAME
        firstClass.medium || '', // Medium - ACTUAL MEDIUM
        firstClass.duration || '', // Program Duration - ACTUAL DURATION
        'Suresh Kumar', // Father Name*
        '9876543211', // Father Contact*
        'Sunita Devi', // Mother Name*
        'Village Rampur, Post Rampur', // Address*
        '', // City
        '', // State
        '', // Email
        '', // Category
        '', // Religion
        '', // Father Occupation
        '', // Mother Occupation
        '', // Alternative Contact
        '', // Last School Name
        '', // Last School Class
        '', // Last School Result
        ''  // Reason for Leaving
      ]);
    }
    
    // Then add rows for each class with class and medium pre-filled
    if (classes.length > 0) {
      classes.forEach(c => {
        classRows.push([
          '', // Student Name*
          '', // Date of Birth*
          '', // Gender*
          '', // Contact Number*
          '', // Admission Date*
          '', // Batch (Year)*
          '1st Year', // Current Year*
          c.name, // Class/Course* - ACTUAL CLASS NAME
          c.medium || '', // Medium - ACTUAL MEDIUM
          c.duration || '', // Program Duration - ACTUAL DURATION
          '', // Father Name*
          '', // Father Contact*
          '', // Mother Name*
          '', // Address*
          '', // City
          '', // State
          '', // Email
          '', // Category
          '', // Religion
          '', // Father Occupation
          '', // Mother Occupation
          '', // Alternative Contact
          '', // Last School Name
          '', // Last School Class
          '', // Last School Result
          ''  // Reason for Leaving
        ]);
      });
    }

    const aoa = [headers, ...classRows];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    
    ws['!cols'] = [
      { wch: 20 }, { wch: 25 }, { wch: 12 }, { wch: 18 },
      { wch: 25 }, { wch: 15 }, { wch: 15 },
      { wch: 20 }, { wch: 12 }, { wch: 18 }, { wch: 20 }, { wch: 18 },
      { wch: 20 }, { wch: 30 }, { wch: 15 }, { wch: 15 }, { wch: 25 }, { wch: 12 }, { wch: 12 },
      { wch: 18 }, { wch: 18 }, { wch: 18 },
      { wch: 20 }, { wch: 18 }, { wch: 18 }, { wch: 20 }
    ];

    const border = {
      top: { style: 'thin', color: { auto: 1 } },
      bottom: { style: 'thin', color: { auto: 1 } },
      left: { style: 'thin', color: { auto: 1 } },
      right: { style: 'thin', color: { auto: 1 } }
    };

    const range = XLSX.utils.decode_range(ws['!ref']);
    
    // Style header row (row 0)
    for (let C = range.s.c; C <= range.e.c; C++) {
      const headerRef = XLSX.utils.encode_cell({ r: 0, c: C });
      ws[headerRef].s = {
        font: { name: 'Arial', sz: 10, bold: true, color: { rgb: 'FFFFFF' } },
        fill: { fgColor: { rgb: '1E40AF' } },
        alignment: { vertical: 'center', horizontal: 'center' },
        border
      };
    }

    // Style data rows
    for (let R = 1; R <= range.e.r; R++) {
      for (let C = range.s.c; C <= range.e.c; C++) {
        const ref = XLSX.utils.encode_cell({ r: R, c: C });
        if (ws[ref]) {
          ws[ref].s = {
            font: { name: 'Arial', sz: 9 },
            alignment: { vertical: 'center', horizontal: 'center' },
            border
          };
        }
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Students Template');
    XLSX.writeFile(wb, 'Bulk_Student_Import_Template.xlsx');
    Swal.fire('Downloaded!', 'Template downloaded successfully. Fill student details in each row.', 'success');
  };

  // Export Failed Records
  const exportFailedRecords = (failedRows) => {
    const headers = [
      'Student Name*', 'Date of Birth (DD-MM-YYYY)*', 'Gender*', 'Contact Number*',
      'Admission Date (DD-MM-YYYY)*', 'Batch (Year)*', 'Current Year*',
      'Class/Course*', 'Medium', 'Program Duration', 'Father Name*', 'Father Contact*',
      'Mother Name*', 'Address*', 'City', 'State', 'Email', 'Category', 'Religion',
      'Father Occupation', 'Mother Occupation', 'Alternative Contact',
      'Last School Name', 'Last School Class', 'Last School Result', 'Reason for Leaving'
    ];

    const aoa = [headers, ...failedRows];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    
    ws['!cols'] = [
      { wch: 20 }, { wch: 25 }, { wch: 12 }, { wch: 18 },
      { wch: 25 }, { wch: 15 }, { wch: 15 },
      { wch: 20 }, { wch: 12 }, { wch: 18 }, { wch: 20 }, { wch: 18 },
      { wch: 20 }, { wch: 30 }, { wch: 15 }, { wch: 15 }, { wch: 25 }, { wch: 12 }, { wch: 12 },
      { wch: 18 }, { wch: 18 }, { wch: 18 },
      { wch: 20 }, { wch: 18 }, { wch: 18 }, { wch: 20 }
    ];

    const border = {
      top: { style: 'thin', color: { auto: 1 } },
      bottom: { style: 'thin', color: { auto: 1 } },
      left: { style: 'thin', color: { auto: 1 } },
      right: { style: 'thin', color: { auto: 1 } }
    };

    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let C = range.s.c; C <= range.e.c; C++) {
      const headerRef = XLSX.utils.encode_cell({ r: 0, c: C });
      ws[headerRef].s = {
        font: { name: 'Arial', sz: 10, bold: true, color: { rgb: 'FFFFFF' } },
        fill: { fgColor: { rgb: 'DC2626' } },
        alignment: { vertical: 'center', horizontal: 'center' },
        border
      };
    }

    for (let R = 1; R <= range.e.r; R++) {
      for (let C = range.s.c; C <= range.e.c; C++) {
        const ref = XLSX.utils.encode_cell({ r: R, c: C });
        if (ws[ref]) {
          ws[ref].s = {
            font: { name: 'Arial', sz: 9 },
            alignment: { vertical: 'center', horizontal: 'center' },
            border
          };
        }
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Failed Records');
    XLSX.writeFile(wb, 'Failed_Student_Records.xlsx');
  };

  // Handle Excel Import
  const handleExcelImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!formData.college) {
      Swal.fire('Select College', 'Please select a college first before importing.', 'warning');
      e.target.value = '';
      return;
    }

    if (!activeSession) {
      Swal.fire('No Active Session', 'Please activate a session for this college first.', 'warning');
      e.target.value = '';
      return;
    }

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      if (jsonData.length === 0) {
        Swal.fire('Empty File', 'No data found in the Excel file.', 'warning');
        e.target.value = '';
        return;
      }

      const students = [];
      const errors = [];
      const failedRows = [];

      for (let i = 0; i < jsonData.length; i++) {
        const row = jsonData[i];
        const rowNum = i + 2;
        const rowErrors = [];

        const student = {
          name: row['Student Name*'] || '',
          dob: row['Date of Birth (DD-MM-YYYY)*'] ? convertDDMMYYYYtoYYYYMMDD(row['Date of Birth (DD-MM-YYYY)*']) : '',
          gender: row['Gender*'] || '',
          contact: String(row['Contact Number*'] || '').replace(/\D/g, ''),
          admissionDate: row['Admission Date (DD-MM-YYYY)*'] ? convertDDMMYYYYtoYYYYMMDD(row['Admission Date (DD-MM-YYYY)*']) : '',
          admissionYear: String(row['Batch (Year)*'] || ''),
          currentYear: row['Current Year*'] || '1st Year',
          fatherName: row['Father Name*'] || '',
          fatherContact: String(row['Father Contact*'] || '').replace(/\D/g, ''),
          motherName: row['Mother Name*'] || '',
          address: row['Address*'] || '',
          city: row['City'] || '',
          state: row['State'] || '',
          email: row['Email'] || '',
          category: row['Category'] || '',
          religion: row['Religion'] || '',
          fatherOccupation: row['Father Occupation'] || '',
          motherOccupation: row['Mother Occupation'] || '',
          alternativeContact: String(row['Alternative Contact'] || '').replace(/\D/g, ''),
          lastSchoolName: row['Last School Name'] || '',
          lastSchoolClass: row['Last School Class'] || '',
          lastSchoolResult: row['Last School Result'] || '',
          leftReason: row['Reason for Leaving'] || '',
          college: formData.college,
          session: activeSession._id,
          country: 'India'
        };

        // Find class by name and medium
        const className = row['Class/Course*'] || '';
        const medium = row['Medium'] || '';
        const foundClass = classes.find(c => {
          const nameMatch = c.name === className;
          const mediumMatch = !medium || c.medium === medium;
          return nameMatch && mediumMatch;
        });

        if (!foundClass) {
          rowErrors.push(`Class "${className}" ${medium ? `with medium "${medium}"` : ''} not found`);
        } else {
          student.class = foundClass._id;
        }

        // Validate required fields
        if (!student.name) rowErrors.push('Student Name is required');
        if (!student.dob) rowErrors.push('Date of Birth is required');
        if (!student.gender) rowErrors.push('Gender is required');
        if (!student.contact || student.contact.length !== 10) rowErrors.push('Valid 10-digit Contact Number is required');
        if (!student.admissionDate) rowErrors.push('Admission Date is required');
        if (!student.fatherName) rowErrors.push('Father Name is required');
        if (!student.fatherContact || student.fatherContact.length !== 10) rowErrors.push('Valid 10-digit Father Contact is required');
        if (!student.motherName) rowErrors.push('Mother Name is required');
        if (!student.address) rowErrors.push('Address is required');

        if (rowErrors.length > 0) {
          errors.push(`Row ${rowNum}: ${rowErrors.join(', ')}`);
          failedRows.push([
            row['Student Name*'] || '',
            row['Date of Birth (DD-MM-YYYY)*'] || '',
            row['Gender*'] || '',
            row['Contact Number*'] || '',
            row['Admission Date (DD-MM-YYYY)*'] || '',
            row['Batch (Year)*'] || '',
            row['Current Year*'] || '',
            row['Class/Course*'] || '',
            row['Medium'] || '',
            row['Program Duration'] || '',
            row['Father Name*'] || '',
            row['Father Contact*'] || '',
            row['Mother Name*'] || '',
            row['Address*'] || '',
            row['City'] || '',
            row['State'] || '',
            row['Email'] || '',
            row['Category'] || '',
            row['Religion'] || '',
            row['Father Occupation'] || '',
            row['Mother Occupation'] || '',
            row['Alternative Contact'] || '',
            row['Last School Name'] || '',
            row['Last School Class'] || '',
            row['Last School Result'] || '',
            row['Reason for Leaving'] || ''
          ]);
        } else {
          students.push(student);
        }
      }

      if (students.length === 0) {
        const result = await Swal.fire({
          icon: 'error',
          title: 'All Records Failed Validation',
          html: `<div style="text-align:left;max-height:300px;overflow-y:auto;">${errors.map(e => `<div>• ${e}</div>`).join('')}</div>`,
          showCancelButton: true,
          confirmButtonText: 'Export Failed Records',
          cancelButtonText: 'Close',
          confirmButtonColor: '#ef4444',
          cancelButtonColor: '#94a3b8'
        });
        
        if (result.isConfirmed) {
          exportFailedRecords(failedRows);
          Swal.fire('Exported!', 'Failed records exported. Fix errors and re-import.', 'success');
        }
        e.target.value = '';
        return;
      }

      // If some records have validation errors, show warning but allow import of valid ones
      if (errors.length > 0) {
        const collegeName = colleges.find(c => c._id === formData.college)?.name || 'Selected College';
        const sessionName = activeSession?.name || 'Active Session';
        
        const result = await Swal.fire({
          icon: 'warning',
          title: 'Some Records Have Errors',
          html: `<div style="margin-bottom:15px;padding:10px;background:#f0f9ff;border-left:4px solid #3b82f6;text-align:left;">
                   <strong>College:</strong> ${collegeName}<br/>
                   <strong>Session:</strong> ${sessionName}<br/>
                   <small style="color:#64748b;">All students will be imported to this college and session</small>
                 </div>
                 <b>${students.length}</b> valid records found.<br/><b>${failedRows.length}</b> records have validation errors and will be skipped.<br/><br/>Do you want to import the valid records?`,
          showCancelButton: true,
          confirmButtonText: 'Yes, Import Valid Records',
          cancelButtonText: 'Cancel',
          confirmButtonColor: '#10b981',
          cancelButtonColor: '#94a3b8',
          footer: '<small>You can export failed records after import to fix and re-import them</small>'
        });

        if (!result.isConfirmed) {
          e.target.value = '';
          return;
        }
      } else {
        const collegeName = colleges.find(c => c._id === formData.college)?.name || 'Selected College';
        const sessionName = activeSession?.name || 'Active Session';
        
        const result = await Swal.fire({
          title: 'Confirm Import',
          html: `<div style="margin-bottom:15px;padding:10px;background:#f0f9ff;border-left:4px solid #3b82f6;text-align:left;">
                   <strong>College:</strong> ${collegeName}<br/>
                   <strong>Session:</strong> ${sessionName}<br/>
                   <small style="color:#64748b;">All students will be imported to this college and session</small>
                 </div>
                 Ready to import <b>${students.length}</b> student(s).<br/>Admission numbers will be auto-generated.<br/><br/>Continue?`,
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: '#10b981',
          cancelButtonColor: '#94a3b8',
          confirmButtonText: 'Yes, Import',
          cancelButtonText: 'Cancel'
        });

        if (!result.isConfirmed) {
          e.target.value = '';
          return;
        }
      }

      setSaving(true);
      
      // Import valid students to backend
      const response = await api.post('/students/bulk-import', { students });
      
      const { success, failed, errors: importErrors } = response.data;
      const totalFailed = failedRows.length + failed;
      const allFailedRows = [...failedRows];

      // Add backend failed records to frontend failed records
      if (response.data.failedRecords) {
        response.data.failedRecords.forEach(rec => {
          allFailedRows.push([
            rec.name || '',
            rec.dob || '',
            rec.gender || '',
            rec.contact || '',
            rec.admissionDate || '',
            rec.admissionYear || '',
            rec.currentYear || '',
            rec.className || '',
            rec.medium || '',
            rec.programDuration || '',
            rec.fatherName || '',
            rec.fatherContact || '',
            rec.motherName || '',
            rec.address || '',
            rec.city || '',
            rec.state || '',
            rec.email || '',
            rec.category || '',
            rec.religion || '',
            rec.fatherOccupation || '',
            rec.motherOccupation || '',
            rec.alternativeContact || '',
            rec.lastSchoolName || '',
            rec.lastSchoolClass || '',
            rec.lastSchoolResult || '',
            rec.leftReason || ''
          ]);
        });
      }

      // Step 1: Show import summary
      await Swal.fire({
        icon: success > 0 ? 'success' : 'error',
        title: 'Import Completed',
        html: `<b>${success}</b> students imported successfully.<br/><b>${totalFailed}</b> failed.`,
        confirmButtonText: 'OK',
        confirmButtonColor: success > 0 ? '#10b981' : '#ef4444'
      });

      // Step 2: If there are failures, show details with export option
      if (totalFailed > 0) {
        const allErrors = [...errors, ...importErrors];
        const result = await Swal.fire({
          icon: 'warning',
          title: 'Failed Records Details',
          html: `<div style="text-align:left;max-height:300px;overflow-y:auto;">${allErrors.map(e => `<div>• ${e}</div>`).join('')}</div>`,
          showCancelButton: true,
          confirmButtonText: 'Export Failed Records',
          cancelButtonText: 'Close',
          confirmButtonColor: '#f59e0b',
          cancelButtonColor: '#94a3b8'
        });
        
        if (result.isConfirmed && allFailedRows.length > 0) {
          exportFailedRecords(allFailedRows);
          await Swal.fire('Exported!', 'Failed records exported. Fix errors and re-import.', 'success');
        }
      }

      setShowBulkImport(false);
      e.target.value = '';
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Import Failed',
        text: error.response?.data?.message || 'Failed to import students.',
        confirmButtonColor: '#ef4444'
      });
      e.target.value = '';
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="add-student-container">

      {/* Header */}
      <div className="add-student-header">
        <button type="button" className="add-student-back-btn" onClick={() => navigate(-1)}>
          <FaArrowLeft /> Back
        </button>
        <h2 className="add-student-title">Admit New Student</h2>
        <button 
          type="button" 
          className="add-student-bulk-import-btn"
          onClick={() => setShowBulkImport(!showBulkImport)}
          title="Bulk Import Students"
        >
          <FaFileExcel /> Bulk Import
        </button>
      </div>

      {/* Bulk Import Section */}
      {showBulkImport && (
        <div className="add-student-bulk-import-section">
          <h3 className="add-student-bulk-title">
            <FaUpload /> Bulk Import Students from Excel
          </h3>
          <div className="add-student-bulk-content">
            <div className="add-student-bulk-step">
              <span className="add-student-bulk-step-num">1</span>
              <div className="add-student-bulk-step-text">
                <strong>Download Template</strong>
                <p>Download the Excel template with sample data and required format</p>
              </div>
              <button 
                type="button" 
                className="add-student-bulk-download-btn"
                onClick={handleDownloadTemplate}
                disabled={!formData.college}
              >
                <FaDownload /> Download Template
              </button>
            </div>
            <div className="add-student-bulk-step">
              <span className="add-student-bulk-step-num">2</span>
              <div className="add-student-bulk-step-text">
                <strong>Fill Student Data</strong>
                <p>Fill the template with student information. Fields marked with * are required</p>
              </div>
            </div>
            <div className="add-student-bulk-step">
              <span className="add-student-bulk-step-num">3</span>
              <div className="add-student-bulk-step-text">
                <strong>Upload Excel File</strong>
                <p>Upload the filled Excel file. Admission numbers will be auto-generated</p>
              </div>
              <input
                ref={excelInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleExcelImport}
                style={{ display: 'none' }}
              />
              <button 
                type="button" 
                className="add-student-bulk-upload-btn"
                onClick={() => excelInputRef.current?.click()}
                disabled={!formData.college || !activeSession}
              >
                <FaUpload /> Upload Excel
              </button>
            </div>
          </div>
          {(!formData.college || !activeSession) && (
            <div className="add-student-bulk-warning">
              ⚠️ Please select a college and ensure an active session exists before importing
            </div>
          )}
        </div>
      )}

      <form className="add-student-form" onSubmit={handleSubmit}>

        {/* ── SECTION 1: Academic Details ── */}
        <div className="add-student-section">
          <h3 className="add-student-section-title"><FaSchool /> Academic Details</h3>
          <div className="add-student-grid add-student-grid-4">

            {/* College */}
            {isAdmin && (
              <div className="add-student-group">
                <label className="add-student-label">College <span className="add-student-req">*</span></label>
                <div className="add-student-input-wrapper">
                  <FaBuilding className="add-student-icon" />
                  <select name="college" value={formData.college} onChange={handleChange} className="add-student-input" required>
                    <option value="">Select College</option>
                    {colleges.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                  </select>
                </div>
              </div>
            )}

            {/* Session — read-only, only shown after college selected */}
            <div className="add-student-group">
              <label className="add-student-label">Session <span className="add-student-req">*</span></label>
              {!formData.college ? (
                <input type="text" className="add-student-input add-student-readonly" value="Select a college first" readOnly disabled />
              ) : activeSession ? (
                <input type="text" className="add-student-input add-student-readonly add-student-session-active" value={activeSession.name} readOnly disabled />
              ) : (
                <div className="add-student-no-session">
                  <input type="text" className="add-student-input add-student-readonly add-student-session-error" value="No active session" readOnly disabled />
                  <span className="add-student-session-hint">
                    <Link to={`${basePath}/sessions`}>Activate a session</Link> first.
                  </span>
                </div>
              )}
            </div>

            {/* Admission Number - auto generated, read only */}
            <div className="add-student-group">
              <label className="add-student-label">Admission Number</label>
              <div className="add-student-input-wrapper">
                <FaIdBadge className="add-student-icon" />
                <input
                  type="text"
                  className="add-student-input add-student-readonly add-student-session-active"
                  value={nextAdmissionNo || (formData.college && activeSession ? 'Generating...' : '—')}
                  readOnly
                  disabled
                  title="Auto-generated after admission"
                />
              </div>
              <span style={{ fontSize: '0.72rem', color: '#64748b' }}>Auto-generated on save</span>
            </div>

            {/* Admission Date */}
            <div className="add-student-group">
              <label className="add-student-label">Admission Date <span className="add-student-req">*</span></label>
              <div className="add-student-input-wrapper">
                <FaCalendarAlt className="add-student-icon" />
                <input type="date" name="admissionDate" value={formData.admissionDate} onChange={handleChange} className="add-student-input" required />
              </div>
            </div>

            {/* Batch — auto from session, read-only, never changes */}
            <div className="add-student-group">
              <label className="add-student-label">Batch <span className="add-student-req">*</span></label>
              <input
                type="text"
                className="add-student-input add-student-readonly add-student-session-active"
                value={formData.admissionYear || (formData.college ? 'No active session' : 'Select college first')}
                readOnly
                disabled
                title="Auto-set from active session — never changes"
              />
              <span style={{ fontSize: '0.72rem', color: '#64748b' }}>Auto-set from session</span>
            </div>

            {/* Current Year — always 1st Year, read-only */}
            <div className="add-student-group">
              <label className="add-student-label">Year <span className="add-student-req">*</span></label>
              <input
                type="text"
                className="add-student-input add-student-readonly add-student-session-active"
                value="1st Year"
                readOnly
                disabled
              />
            </div>

          </div>
        </div>

        {/* ── SECTION 2: Personal Information ── */}
        <div className="add-student-section">
          <h3 className="add-student-section-title"><FaUserGraduate /> Personal Information</h3>
          <div className="add-student-grid add-student-grid-4">

            <div className="add-student-group">
              <label className="add-student-label">Student Full Name <span className="add-student-req">*</span></label>
              <div className="add-student-input-wrapper">
                <FaUserGraduate className="add-student-icon" />
                <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Full Name" className="add-student-input" required />
              </div>
            </div>

            <div className="add-student-group">
              <label className="add-student-label">Date of Birth <span className="add-student-req">*</span></label>
              <div className="add-student-input-wrapper">
                <FaCalendarAlt className="add-student-icon" />
                <input type="date" name="dob" value={formData.dob} onChange={handleChange} className="add-student-input" required />
              </div>
            </div>

            <div className="add-student-group">
              <label className="add-student-label">Gender <span className="add-student-req">*</span></label>
              <div className="add-student-input-wrapper">
                <FaVenusMars className="add-student-icon" />
                <select name="gender" value={formData.gender} onChange={handleChange} className="add-student-input" required>
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Transgender">Transgender</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            <div className="add-student-group">
              <label className="add-student-label">Category</label>
              <select name="category" value={formData.category} onChange={handleChange} className="add-student-input">
                <option value="">Select Category</option>
                <option value="General">General</option>
                <option value="OBC">OBC</option>
                <option value="SC">SC</option>
                <option value="ST">ST</option>
                <option value="EWS">EWS</option>
                <option value="Minority">Minority</option>
              </select>
            </div>

            <div className="add-student-group">
              <label className="add-student-label">Religion</label>
              <select name="religion" value={formData.religion} onChange={handleChange} className="add-student-input">
                <option value="">Select Religion</option>
                <option value="Hindu">Hindu</option>
                <option value="Muslim">Muslim</option>
                <option value="Christian">Christian</option>
                <option value="Buddhist">Buddhist</option>
                <option value="Jain">Jain</option>
              </select>
            </div>

            <div className="add-student-group">
              <label className="add-student-label">Contact Number <span className="add-student-req">*</span></label>
              <div className="add-student-input-wrapper">
                <FaPhoneAlt className="add-student-icon" />
                <input type="text" name="contact" value={formData.contact} onChange={handleChange} placeholder="10-digit mobile" className="add-student-input" required />
              </div>
            </div>

            <div className="add-student-group">
              <label className="add-student-label">Email Address</label>
              <div className="add-student-input-wrapper">
                <FaEnvelope className="add-student-icon" />
                <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="student@example.com" className="add-student-input" />
              </div>
            </div>

            {/* Photo */}
            <div className="add-student-group">
              <label className="add-student-label">Student Photo</label>
              <div className="add-student-photo-row">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="add-student-file-btn"
                  id="student-photo-input"
                />
                {photoPreview && (
                  <button
                    type="button"
                    className="add-student-preview-btn"
                    onClick={() => window.open(photoPreview, '_blank')}
                  >
                    <FaEye /> Preview
                  </button>
                )}
              </div>
              {photoPreview && (
                <span className="add-student-photo-name">{photo?.name}</span>
              )}
            </div>

            {/* Education Document */}
            <div className="add-student-group">
              <label className="add-student-label">Education Document (PDF Only)</label>
              <div className="add-student-photo-row">
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={handleEducationDocChange}
                  className="add-student-file-btn"
                  id="education-document-input"
                />
              </div>
              {educationDocName && (
                <span className="add-student-photo-name" style={{color:'#10b981'}}>✓ {educationDocName}</span>
              )}
              <span style={{ fontSize: '0.72rem', color: '#64748b', display: 'block', marginTop: '4px' }}>Upload educational certificates (PDF only)</span>
            </div>

          </div>
        </div>

        {/* ── SECTION 3: Parent Details ── */}
        <div className="add-student-section">
          <h3 className="add-student-section-title"><FaUserTie /> Parent Details</h3>
          <div className="add-student-grid add-student-grid-4">
            <div className="add-student-group">
              <label className="add-student-label">Father's Name <span className="add-student-req">*</span></label>
              <input type="text" name="fatherName" value={formData.fatherName} onChange={handleChange} placeholder="Father's Name" className="add-student-input" required />
            </div>
            <div className="add-student-group">
              <label className="add-student-label">Father's Occupation</label>
              <input type="text" name="fatherOccupation" value={formData.fatherOccupation} onChange={handleChange} placeholder="e.g. Business, Service" className="add-student-input" />
            </div>
            <div className="add-student-group">
              <label className="add-student-label">Father's Contact <span className="add-student-req">*</span></label>
              <div className="add-student-input-wrapper">
                <FaPhoneAlt className="add-student-icon" />
                <input type="text" name="fatherContact" value={formData.fatherContact} onChange={handleChange} placeholder="Father's mobile number" className="add-student-input" required />
              </div>
            </div>
            <div className="add-student-group">
              <label className="add-student-label">Alternative Contact</label>
              <div className="add-student-input-wrapper">
                <FaPhoneAlt className="add-student-icon" />
                <input type="text" name="alternativeContact" value={formData.alternativeContact} onChange={handleChange} placeholder="Alternative number" className="add-student-input" />
              </div>
            </div>
            <div className="add-student-group">
              <label className="add-student-label">Mother's Name <span className="add-student-req">*</span></label>
              <input type="text" name="motherName" value={formData.motherName} onChange={handleChange} placeholder="Mother's Name" className="add-student-input" required />
            </div>
            <div className="add-student-group">
              <label className="add-student-label">Mother's Occupation</label>
              <input type="text" name="motherOccupation" value={formData.motherOccupation} onChange={handleChange} placeholder="e.g. Housewife, Teacher" className="add-student-input" />
            </div>
          </div>
        </div>

        {/* ── SECTION 4: Address ── */}
        <div className="add-student-section">
          <h3 className="add-student-section-title"><FaMapMarkerAlt /> Address</h3>
          <div className="add-student-grid add-student-grid-4">
            <div className="add-student-group add-student-full-width">
              <label className="add-student-label">Address <span className="add-student-req">*</span></label>
              <div className="add-student-input-wrapper">
                <FaMapMarkerAlt className="add-student-icon" />
                <input type="text" name="address" value={formData.address} onChange={handleChange} placeholder="Street / Colony / Village" className="add-student-input" required />
              </div>
            </div>
            <div className="add-student-group">
              <label className="add-student-label">City</label>
              <input type="text" name="city" value={formData.city} onChange={handleChange} placeholder="City" className="add-student-input" />
            </div>
            <div className="add-student-group">
              <label className="add-student-label">State</label>
              <StateSelect
                value={formData.state}
                onChange={v => setFormData(prev => ({ ...prev, state: v }))}
              />
            </div>
            <div className="add-student-group">
              <label className="add-student-label">Country</label>
              <input type="text" className="add-student-input add-student-readonly" value="India" readOnly disabled />
            </div>
          </div>
        </div>

        {/* ── SECTION 5: Previous School & Admission Class ── */}
        <div className="add-student-section">
          <h3 className="add-student-section-title"><FaInfoCircle /> Previous School Details</h3>
          <div className="add-student-grid add-student-grid-4">

            <div className="add-student-group add-student-span-2">
              <label className="add-student-label">Last School Name</label>
              <input type="text" name="lastSchoolName" value={formData.lastSchoolName} onChange={handleChange} placeholder="Name of previous school" className="add-student-input" />
            </div>

            <div className="add-student-group">
              <label className="add-student-label">Last Class Attended</label>
              <input type="text" name="lastSchoolClass" value={formData.lastSchoolClass} onChange={handleChange} placeholder="e.g. 5th Standard" className="add-student-input" />
            </div>

            <div className="add-student-group">
              <label className="add-student-label">Result / Grade</label>
              <input type="text" name="lastSchoolResult" value={formData.lastSchoolResult} onChange={handleChange} placeholder="e.g. Passed, 85%" className="add-student-input" />
            </div>

            <div className="add-student-group add-student-full-width">
              <label className="add-student-label">Reason for Leaving</label>
              <input type="text" name="leftReason" value={formData.leftReason} onChange={handleChange} placeholder="e.g. Relocation, Upgraded" className="add-student-input" />
            </div>

            {/* Admission Class */}
            <div className="add-student-group add-student-span-2">
              <label className="add-student-label">Admitting to Class <span className="add-student-req">*</span></label>
              <select name="classId" value={formData.classId} onChange={handleChange} className="add-student-input" required disabled={!formData.college}>
                <option value="">{formData.college ? 'Select Class' : 'Select college first'}</option>
                {classes.map(c => <option key={c._id} value={c._id}>{c.name}{c.medium ? ` (${c.medium})` : ''}</option>)}
              </select>
              {formData.college && classes.length === 0 && (
                <span className="add-student-session-hint">No classes found. <Link to={`${basePath}/classes`}>Add classes</Link> first.</span>
              )}
            </div>

            {/* Program Duration — auto-filled from selected class */}
            <div className="add-student-group">
              <label className="add-student-label">Program Duration</label>
              <input
                type="text"
                className="add-student-input add-student-readonly"
                value={programDuration ? `${programDuration} Year${parseInt(programDuration) > 1 ? 's' : ''}` : '—'}
                readOnly
                disabled
              />
            </div>

          </div>
        </div>

        {/* ── Action Buttons ── */}
        <div className="add-student-action-bar">
          <button type="button" className="add-student-btn-cancel" onClick={() => navigate(-1)} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="add-student-btn-submit" disabled={saving}>
            {saving ? 'Admitting...' : <><FaSave /> Admit Student</>}
          </button>
        </div>

      </form>
    </div>
  );
};

export default AddStudent;
